import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock, ChevronLeft, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';
import { userService } from '../services/userService';
import toast from 'react-hot-toast';

const ChangePassword = () => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const togglePassword = (field) => {
    setShowPasswords({ ...showPasswords, [field]: !showPasswords[field] });
  };

  const validatePassword = (password) => {
    const requirements = [
      { met: password.length >= 6, text: 'At least 6 characters' },
      { met: /[A-Z]/.test(password), text: 'One uppercase letter' },
      { met: /[a-z]/.test(password), text: 'One lowercase letter' },
      { met: /[0-9]/.test(password), text: 'One number' },
    ];
    return requirements;
  };

  const passwordRequirements = validatePassword(formData.newPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (!passwordRequirements.every(req => req.met)) {
      toast.error('Please meet all password requirements');
      return;
    }

    setLoading(true);
    try {
      await userService.updateProfile({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });
      toast.success('Password changed successfully!');
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const PasswordInput = ({ name, label, placeholder, showField }) => (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div className="relative">
        <input
          type={showField ? 'text' : 'password'}
          name={name}
          value={formData[name]}
          onChange={handleInputChange}
          placeholder={placeholder}
          className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
          required
        />
        <button
          type="button"
          onClick={() => togglePassword(showField)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
        >
          {showField ? <EyeOff size={20} /> : <Eye size={20} />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <Link to="/store" className="p-2 hover:bg-white/20 rounded-lg">
            <ChevronLeft size={24} />
          </Link>
          <Lock size={28} />
          <h1 className="text-xl font-bold">Change Password</h1>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <PasswordInput
              name="currentPassword"
              label="Current Password"
              placeholder="Enter current password"
              showField="current"
            />

            <PasswordInput
              name="newPassword"
              label="New Password"
              placeholder="Enter new password"
              showField="new"
            />

            {/* Password Requirements */}
            {formData.newPassword && (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Password Requirements:</p>
                <div className="grid grid-cols-2 gap-2">
                  {passwordRequirements.map((req, index) => (
                    <div key={index} className="flex items-center gap-2">
                      {req.met ? (
                        <CheckCircle size={16} className="text-green-500" />
                      ) : (
                        <XCircle size={16} className="text-gray-300" />
                      )}
                      <span className={`text-sm ${req.met ? 'text-green-600' : 'text-gray-500'}`}>
                        {req.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <PasswordInput
              name="confirmPassword"
              label="Confirm New Password"
              placeholder="Confirm new password"
              showField="confirm"
            />

            {/* Password Match Indicator */}
            {formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
              <p className="text-red-500 text-sm flex items-center gap-2">
                <XCircle size={16} />
                Passwords do not match
              </p>
            )}

            {formData.confirmPassword && formData.newPassword === formData.confirmPassword && (
              <p className="text-green-500 text-sm flex items-center gap-2">
                <CheckCircle size={16} />
                Passwords match
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:shadow-lg transition-all font-medium disabled:opacity-50 mt-4"
            >
              {loading ? 'Changing Password...' : 'Change Password'}
            </button>
          </form>

          {/* Tips */}
          <div className="mt-6 p-4 bg-blue-50 rounded-xl">
            <h3 className="font-medium text-blue-800 mb-2">Password Tips:</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Use a combination of letters, numbers, and symbols</li>
              <li>• Avoid using personal information like birthdays</li>
              <li>• Don't use the same password for multiple accounts</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;

